import React, { useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormHelperText from '@mui/material/FormHelperText'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import API from '../services/API'
import { signupPalette } from './signupTheme'

// Same bounds as the server (helpers/subscriptionAgreement/acceptance.js),
// which trims the role and rejects anything outside them with `roleRequired`.
const ROLE_MIN_LENGTH = 2
const ROLE_MAX_LENGTH = 120

// The three statements the server records, one tick each. They are separate
// on purpose: the acceptance evidence stores three booleans and the server
// refuses the acceptance unless every one of them is strictly true.
const CONFIRMATION_KEYS = [
  'reviewedAgreement',
  'acceptsForOrganisation',
  'hasAuthority',
]

export const NO_CONFIRMATIONS = {
  reviewedAgreement: false,
  acceptsForOrganisation: false,
  hasAuthority: false,
}

export const EMPTY_ACCEPTANCE = { role: '', ...NO_CONFIRMATIONS }

export const isRoleValid = (role) => {
  const trimmed = String(role ?? '').trim()
  return trimmed.length >= ROLE_MIN_LENGTH && trimmed.length <= ROLE_MAX_LENGTH
}

export const isAcceptanceComplete = (value) =>
  isRoleValid(value?.role) &&
  CONFIRMATION_KEYS.every((key) => value?.[key] === true)

const CADENCE_LABELS = { monthly: 'Monthly', annually: 'Annually' }

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// '2026-10-01' → '1 October 2026'. Split by hand rather than parsed: a date
// only string becomes midnight UTC in a Date, which a browser west of
// Greenwich would print as the day before.
const formatDateOnly = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ''))
  if (!match) return String(value ?? '')
  const [, year, month, day] = match
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`
}

const summaryErrorFor = (res) => {
  const code = res?.data?.error
  if (res?.status === 400 && code === 'invalidPlan') {
    return 'Please choose a plan above to see your Plan Summary.'
  }
  if (res?.status === 400 && code === 'priceNotFound') {
    return 'We could not find the current price for this plan, so its Plan Summary cannot be shown. Please choose another plan or try again shortly.'
  }
  if (res?.status === 409 && code === 'emailNotVerified') {
    return 'Your email address has not been verified yet. Please refresh this page to verify it, then review your Plan Summary.'
  }
  if (res?.status === 429) {
    return 'Too many requests have been made in a short time. Please wait a few minutes and try again.'
  }
  return 'We could not load your Plan Summary. Please try again.'
}

const sectionLabelSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'rgba(36,47,64,0.55)',
  mb: 0.5,
}

// nowrap keeps a short value ("Not included", "500") on one line and lets the
// label wrap instead; long values (organisation, price text) wrap as normal.
const SummaryRow = ({ label, value, nowrap }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    gap={2}
    sx={{ py: 0.5, borderBottom: '1px solid rgba(36,47,64,0.06)' }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: signupPalette.primary.main,
        textAlign: 'right',
        ...(nowrap ? { whiteSpace: 'nowrap', flexShrink: 0 } : {}),
      }}
    >
      {value}
    </Typography>
  </Stack>
)

// The agreement part of the payment step: the Plan Summary the server
// generated for the chosen plan, a link to the Business Subscription
// Agreement, the accepting person's role and the three confirmations that
// gate checkout.
//
// Nothing here is worked out in the browser. The summary (package, price,
// cadence, allowances, agreement version) is displayed exactly as the server
// sent it, and the parent echoes the version and fingerprint back when it
// posts the acceptance, so what is recorded is always what the server
// generated and the person saw.
//
// The summary is fetched on mount and again whenever the plan (or reloadKey)
// changes, and every (re)load clears the three ticks: they are statements
// about the summary on screen, so they cannot carry over to a different one.
//
// value / onChange: { role, reviewedAgreement, acceptsForOrganisation,
//   hasAuthority }; onChange is called with an updater `(current) => next`,
//   so the parent can pass its state setter straight in.
// onSummaryChange: null while no summary is on screen, otherwise
//   { lookupKey, agreementVersion, fingerprint, accepted } as the server sent.
// requireConfirmation: ask for the confirmations even if the server reports
//   this summary as already accepted (set once a checkout has been refused
//   for want of a matching acceptance, so the person is never left with
//   nothing to confirm and nothing to press).
// onSignupMoved(res): returns true when the response shows the signup is no
//   longer waiting for payment and the parent has taken over (invalid link,
//   already provisioned, provisioning under way).
// errors: { role: string, confirmations: boolean } from a refused acceptance.
const PlanSummaryAcceptance = ({
  partialSignUpID,
  lookupKey,
  companyName,
  reloadKey,
  requireConfirmation,
  value,
  onChange,
  onSummaryChange,
  onSignupMoved,
  errors,
  disabled,
}) => {
  // 'loading' | 'ready' | 'error'
  const [status, setStatus] = useState('loading')
  const [summary, setSummary] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [retryCount, setRetryCount] = useState(0)
  const [roleTouched, setRoleTouched] = useState(false)
  const [roleEditedSinceError, setRoleEditedSinceError] = useState(false)
  const [confirmAgain, setConfirmAgain] = useState(false)

  // Read inside the fetch effect without making the effect depend on it.
  const valueRef = useRef(value)
  valueRef.current = value

  useEffect(() => {
    let cancelled = false

    setSummary(null)
    setLoadError('')
    onSummaryChange(null)

    // Ticks never survive a (re)load. Say so when some were set, rather than
    // silently unticking boxes the person had already ticked.
    if (CONFIRMATION_KEYS.some((key) => valueRef.current?.[key])) {
      setConfirmAgain(true)
    }
    onChange((current) => ({ ...current, ...NO_CONFIRMATIONS }))

    if (!partialSignUpID || !lookupKey) {
      setStatus('error')
      setLoadError('Please choose a plan above to see your Plan Summary.')
      return undefined
    }

    setStatus('loading')

    const load = async () => {
      try {
        const res = await API.get(
          '/new-tenant-plan-summary/' + partialSignUpID,
          { params: { lookupKey }, skipGlobalErrorHandler: true },
        )
        if (cancelled) return

        if (
          res.status === 200 &&
          res.data?.planSummary &&
          res.data?.fingerprint &&
          res.data?.agreement?.version
        ) {
          setSummary(res.data)
          setStatus('ready')
          onSummaryChange({
            lookupKey,
            agreementVersion: res.data.agreement.version,
            fingerprint: res.data.fingerprint,
            accepted: res.data.accepted === true && !requireConfirmation,
          })
          return
        }

        if (onSignupMoved?.(res)) return

        setLoadError(summaryErrorFor(res))
        setStatus('error')
      } catch (e) {
        if (cancelled) return
        setLoadError(summaryErrorFor(null))
        setStatus('error')
      }
    }

    load()

    return () => {
      cancelled = true
    }
    // Refetch only when the signup, the plan or a reload request changes —
    // the callbacks are the parent's state setters and handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partialSignUpID, lookupKey, reloadKey, retryCount])

  // A role error from a refused acceptance stands until the role is edited.
  useEffect(() => {
    setRoleEditedSinceError(false)
  }, [errors])

  const planSummary = summary?.planSummary
  const agreement = summary?.agreement
  const acceptedBy = summary?.acceptedBy
  const alreadyAccepted = summary?.accepted === true && !requireConfirmation
  const organisation =
    planSummary?.organisationName || companyName || 'your organisation'
  const agreementTitle =
    agreement?.title || 'AmbuNet Business Subscription Agreement'
  // Link to the exact version the summary names, so the text read is the text
  // the acceptance is recorded against.
  const agreementHref = agreement?.version
    ? `/subscription-agreement/${encodeURIComponent(agreement.version)}`
    : '/subscription-agreement'

  const roleError =
    (!roleEditedSinceError && errors?.role) ||
    (roleTouched && !isRoleValid(value?.role)
      ? `Please enter your role in the organisation (${ROLE_MIN_LENGTH} to ${ROLE_MAX_LENGTH} characters).`
      : '')

  const confirmations = [
    {
      key: 'reviewedAgreement',
      label: (
        <>
          I have reviewed the {agreementTitle}
          {agreement?.version ? ` (version ${agreement.version})` : ''}
        </>
      ),
    },
    {
      key: 'acceptsForOrganisation',
      label: <>I accept the Agreement on behalf of {organisation}</>,
    },
    {
      key: 'hasAuthority',
      label: <>I have authority to bind {organisation} to this Agreement</>,
    },
  ]

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'rgba(36,47,64,0.12)',
        bgcolor: '#fff',
        boxShadow: '0 4px 16px rgba(36,47,64,0.06)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          bgcolor: 'rgba(36,47,64,0.04)',
          borderBottom: '1px solid rgba(36,47,64,0.10)',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.05rem',
            color: signupPalette.primary.main,
          }}
        >
          Your Plan Summary
        </Typography>
        <Typography variant="caption" color="text.secondary">
          What {organisation} is agreeing to. It is recorded with your
          acceptance of the Agreement.
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {status === 'loading' && (
          <Stack spacing={1} aria-busy="true" aria-label="Loading Plan Summary">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((row) => (
              <Skeleton key={row} height={28} />
            ))}
          </Stack>
        )}

        {status === 'error' && (
          <Alert
            severity={lookupKey ? 'warning' : 'info'}
            action={
              lookupKey ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setRetryCount((count) => count + 1)}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Try again
                </Button>
              ) : undefined
            }
          >
            {loadError}
          </Alert>
        )}

        {status === 'ready' && planSummary && (
          <Stack spacing={2.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 4,
                rowGap: 2.5,
              }}
            >
              <Box>
                <Typography sx={sectionLabelSx}>Your subscription</Typography>
                <SummaryRow label="Organisation" value={organisation} />
                <SummaryRow label="Package" value={planSummary.package?.name} />
                <SummaryRow
                  label="Subscription fee"
                  value={planSummary.pricing?.text ?? 'as invoiced'}
                />
                <SummaryRow
                  label="Billing cadence"
                  value={
                    CADENCE_LABELS[planSummary.billingCadence] ??
                    planSummary.billingCadence ??
                    'as invoiced'
                  }
                />
                <SummaryRow
                  label="Currency"
                  value={planSummary.currency ?? 'as invoiced'}
                />
              </Box>

              <Box>
                <Typography sx={sectionLabelSx}>Included allowances</Typography>
                {(planSummary.allowances ?? []).map((row) => (
                  <SummaryRow
                    key={row.metric}
                    label={row.label}
                    value={row.includedText}
                    nowrap
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography sx={sectionLabelSx}>Agreement</Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={1.5}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: signupPalette.primary.main }}
                  >
                    {agreementTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Version {agreement.version} · effective{' '}
                    {formatDateOnly(agreement.effectiveDate)}
                  </Typography>
                </Box>
                <Button
                  component="a"
                  href={agreementHref}
                  target="_blank"
                  rel="noopener"
                  variant="outlined"
                  startIcon={<DescriptionOutlinedIcon />}
                  endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    flexShrink: 0,
                    color: signupPalette.third.dark,
                    borderColor: signupPalette.third.main,
                    '&:hover': {
                      borderColor: signupPalette.third.dark,
                      bgcolor: 'rgba(35,154,164,0.06)',
                    },
                  }}
                >
                  Read the Agreement
                </Button>
              </Stack>
            </Box>

            {alreadyAccepted ? (
              <Alert severity="success">
                <AlertTitle>Agreement accepted</AlertTitle>
                {acceptedBy?.name || 'You'} {acceptedBy?.name ? 'has' : 'have'}{' '}
                already accepted the {agreementTitle} (version{' '}
                {agreement.version}) and this Plan Summary on behalf of{' '}
                {organisation}. You can continue to secure checkout. If you
                choose a different plan you will be asked to confirm again.
              </Alert>
            ) : (
              <Stack
                spacing={1.5}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 3,
                  bgcolor: 'rgba(35,154,164,0.05)',
                  border: '1px solid rgba(35,154,164,0.25)',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: signupPalette.primary.main,
                    }}
                  >
                    Accept on behalf of {organisation}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    Accepting as{' '}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: signupPalette.primary.main,
                      }}
                    >
                      {acceptedBy?.name || 'the person signing up'}
                    </Box>
                    {acceptedBy?.email ? ` (${acceptedBy.email})` : ''}. Your
                    name, email address and role are recorded with the
                    acceptance.
                  </Typography>
                </Box>

                {confirmAgain && (
                  <Alert severity="info">
                    Your Plan Summary has been updated, so the confirmations
                    below were cleared. Please review it and confirm again.
                  </Alert>
                )}

                <TextField
                  label="Your role in the organisation"
                  fullWidth
                  size="small"
                  value={value?.role ?? ''}
                  onChange={(e) => {
                    const role = e.target.value
                    setRoleEditedSinceError(true)
                    onChange((current) => ({ ...current, role }))
                  }}
                  onBlur={() => setRoleTouched(true)}
                  required
                  disabled={disabled}
                  error={Boolean(roleError)}
                  helperText={
                    roleError ||
                    'For example Managing Director, Operations Manager or Company Secretary.'
                  }
                  autoComplete="organization-title"
                  inputProps={{ maxLength: ROLE_MAX_LENGTH }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
                />

                <Stack spacing={0.5}>
                  {confirmations.map(({ key, label }) => {
                    const checked = value?.[key] === true
                    const showError = Boolean(errors?.confirmations) && !checked
                    // Each label is top-aligned with its tick box so a long
                    // organisation name can wrap beneath it on a phone. The
                    // label carries its own asterisk (each box is individually
                    // required), so the one MUI would add on a new line for a
                    // required control is switched off.
                    return (
                      <FormControl key={key} error={showError}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={checked}
                                disabled={disabled}
                                required
                                onChange={(e) => {
                                  const ticked = e.target.checked
                                  setConfirmAgain(false)
                                  onChange((current) => ({
                                    ...current,
                                    [key]: ticked,
                                  }))
                                }}
                                sx={{
                                  '&.Mui-checked': {
                                    color: signupPalette.third.main,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{
                                  pt: '11px',
                                  color: showError
                                    ? 'error.main'
                                    : signupPalette.primary.main,
                                  overflowWrap: 'anywhere',
                                }}
                              >
                                {label}
                                <Box
                                  component="span"
                                  aria-hidden
                                  sx={{ color: 'error.main' }}
                                >
                                  {' *'}
                                </Box>
                              </Typography>
                            }
                            required={false}
                            sx={{ alignItems: 'flex-start', mr: 0 }}
                          />
                        </FormGroup>
                        {showError && (
                          <FormHelperText sx={{ ml: 4 }}>
                            Please tick this box to continue.
                          </FormHelperText>
                        )}
                      </FormControl>
                    )
                  })}
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  )
}

export default PlanSummaryAcceptance
